// src/components/admin/page-management/VideoUploader.js
import React, { useEffect, useState } from "react";
import axios from "../../../utils/axiosHandler";

const VideoUploader = ({ onVideoUpload, pageSlug, onOverlayConfigChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");
  const [overlay, setOverlay] = useState({
    enabled: false,
    color: "#000000",
    opacity: 0.5,
  });

  // Handle overlay change
  const handleOverlayChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    const updatedOverlay = {
      ...overlay,
      [name]: newValue,
    };

    setOverlay(updatedOverlay);
    if (onOverlayConfigChange) {
      onOverlayConfigChange(updatedOverlay);
    }
  };

  // Log the pageSlug prop whenever it changes
  useEffect(() => {
    console.log("VideoUploader received pageSlug:", pageSlug);
    setDebugInfo(`Current page slug: ${pageSlug || "none"}`);
  }, [pageSlug]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (limit to 1000MB)
    if (file.size > 1000 * 1024 * 1024) {
      setError("Video file is too large. Maximum size is 1GB.");
      return;
    }

    // Check file type
    const validTypes = ["video/mp4", "video/webm", "video/ogg"];
    if (!validTypes.includes(file.type)) {
      setError("Invalid video format. Please use MP4, WebM, or OGG.");
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append("video", file);

    // Clean the slug
    const cleanSlug = pageSlug
      ? pageSlug.replace(/[^a-zA-Z0-9-_]/g, "")
      : "shared";

    // Add to form data too
    formData.append("pageSlug", cleanSlug);

    // Debug logs
    console.log(`Uploading video for page: ${cleanSlug}`);
    setDebugInfo(`Uploading for: ${cleanSlug}`);

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Get token from localStorage directly
      const token = localStorage.getItem("token");

      // Use both query parameter and form data for the page slug (matching your existing pattern)
      const response = await axios.post(
        `/api/upload-page-video?pageSlug=${cleanSlug}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      setUploading(false);

      // Call the callback with the uploaded video URL
      if (onVideoUpload && response.data && response.data.filePath) {
        console.log("Video uploaded successfully:", response.data.filePath);
        setDebugInfo(`Upload success: ${response.data.filePath}`);
        onVideoUpload(response.data.filePath);
      }
    } catch (err) {
      setUploading(false);
      setError(err.response?.data?.error || "Error uploading video");
      setDebugInfo(`Upload error: ${err.message}`);
      console.error("Upload error:", err);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center space-x-2 mb-3">
        <label className="cursor-pointer px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm">
          <span>Choose Video File</span>
          <input
            type="file"
            accept="video/mp4,video/webm,video/ogg"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>
        {uploading && (
          <div className="flex-1 relative h-8 bg-gray-200 rounded overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-blue-500 transition-all"
              style={{ width: `${uploadProgress}%` }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center text-xs">
              {uploadProgress}%
            </div>
          </div>
        )}
      </div>

      {/* Overlay Controls */}
      <div className="border rounded p-3 mb-3 bg-gray-50">
        <h4 className="font-medium mb-2">Video Overlay</h4>
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="overlay-enabled"
            name="enabled"
            checked={overlay.enabled}
            onChange={handleOverlayChange}
            className="mr-2"
          />
          <label htmlFor="overlay-enabled" className="text-sm">
            Enable Overlay
          </label>
        </div>

        {overlay.enabled && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className="block text-xs mb-1">Overlay Color</label>
                <div className="flex">
                  <input
                    type="color"
                    name="color"
                    value={overlay.color}
                    onChange={handleOverlayChange}
                    className="h-8 w-8 p-0 border rounded"
                  />
                  <input
                    type="text"
                    name="color"
                    value={overlay.color}
                    onChange={handleOverlayChange}
                    className="flex-1 ml-2 p-1 border rounded text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">
                  Opacity ({Math.round(overlay.opacity * 100)}%)
                </label>
                <input
                  type="range"
                  name="opacity"
                  min="0"
                  max="1"
                  step="0.05"
                  value={overlay.opacity}
                  onChange={handleOverlayChange}
                  className="w-full"
                />
              </div>
            </div>
            <div className="h-10 rounded overflow-hidden relative">
              <div className="absolute inset-0 bg-gray-200 bg-checkerboard"></div>
              <div
                className="absolute inset-0"
                style={{
                  backgroundColor: overlay.color,
                  opacity: overlay.opacity,
                }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center text-xs text-white">
                Preview
              </div>
            </div>
          </>
        )}
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

      <p className="text-xs text-gray-500">
        Upload an MP4, WebM, or OGG video file (max 1GB) or provide a URL
        above
      </p>
    </div>
  );
};

export default VideoUploader;

// // before overlay
// // src/components/admin/page-management/VideoUploader.js
// import React, { useState, useEffect } from 'react';

// const VideoUploader = ({ onVideoUpload, pageSlug }) => {
//   const [uploading, setUploading] = useState(false);
//   const [uploadProgress, setUploadProgress] = useState(0);
//   const [error, setError] = useState(null);
//   const [debugInfo, setDebugInfo] = useState('');

//   // Log the pageSlug prop whenever it changes
//   useEffect(() => {
//     console.log('VideoUploader received pageSlug:', pageSlug);
//     setDebugInfo(`Current page slug: ${pageSlug || 'none'}`);
//   }, [pageSlug]);

//   const handleFileChange = async (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     // Check file size (limit to 100MB)
//     if (file.size > 100 * 1024 * 1024) {
//       setError('Video file is too large. Maximum size is 100MB.');
//       return;
//     }

//     // Check file type
//     const validTypes = ['video/mp4', 'video/webm', 'video/ogg'];
//     if (!validTypes.includes(file.type)) {
//       setError('Invalid video format. Please use MP4, WebM, or OGG.');
//       return;
//     }

//     // Create form data
//     const formData = new FormData();
//     formData.append('video', file);

//     // Clean the slug
//     const cleanSlug = pageSlug ? pageSlug.replace(/[^a-zA-Z0-9-_]/g, '') : 'shared';

//     // Add to form data too
//     formData.append('pageSlug', cleanSlug);

//     // Debug logs
//     console.log(`Uploading video for page: ${cleanSlug}`);
//     setDebugInfo(`Uploading for: ${cleanSlug}`);

//     setUploading(true);
//     setUploadProgress(0);
//     setError(null);

//     try {
//       // Get token from localStorage directly
//       const token = localStorage.getItem('token');

//       // Use both query parameter and form data for the page slug (matching your existing pattern)
//       const response = await axios.post(`/api/upload-page-video?pageSlug=${cleanSlug}`, formData, {
//         headers: {
//           'Content-Type': 'multipart/form-data',
//           'Authorization': `Bearer ${token}`
//         },
//         onUploadProgress: (progressEvent) => {
//           const percentCompleted = Math.round(
//             (progressEvent.loaded * 100) / progressEvent.total
//           );
//           setUploadProgress(percentCompleted);
//         },
//       });

//       setUploading(false);

//       // Call the callback with the uploaded video URL
//       if (onVideoUpload && response.data && response.data.filePath) {
//         console.log('Video uploaded successfully:', response.data.filePath);
//         setDebugInfo(`Upload success: ${response.data.filePath}`);
//         onVideoUpload(response.data.filePath);
//       }
//     } catch (err) {
//       setUploading(false);
//       setError(err.response?.data?.error || 'Error uploading video');
//       setDebugInfo(`Upload error: ${err.message}`);
//       console.error('Upload error:', err);
//     }
//   };

//   return (
//     <div className="mb-4">
//       <div className="flex items-center space-x-2">
//         <label className="cursor-pointer px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm">
//           <span>Choose Video File</span>
//           <input
//             type="file"
//             accept="video/mp4,video/webm,video/ogg"
//             className="hidden"
//             onChange={handleFileChange}
//             disabled={uploading}
//           />
//         </label>
//         {uploading && (
//           <div className="flex-1 relative h-8 bg-gray-200 rounded overflow-hidden">
//             <div
//               className="absolute top-0 left-0 h-full bg-blue-500 transition-all"
//               style={{ width: `${uploadProgress}%` }}
//             ></div>
//             <div className="absolute inset-0 flex items-center justify-center text-xs">
//               {uploadProgress}%
//             </div>
//           </div>
//         )}
//       </div>

//       {error && (
//         <p className="text-red-500 text-sm mt-1">{error}</p>
//       )}

//       {/* Debug info displayed on the UI */}
//       <p className="text-xs bg-gray-100 p-1 mt-1">{debugInfo}</p>

//       <p className="text-xs text-gray-500 mt-1">
//         Upload an MP4, WebM, or OGG video file (max 100MB) or provide a URL above
//       </p>
//     </div>
//   );
// };

// export default VideoUploader;
